/**
 * Палитра поиска (порт CommandPalette из референса advanced-3d (7)):
 * умный движок (опечатки/транслит/раскладка/синонимы), табы видов,
 * «Открыто сейчас», быстрые запросы, недавние цели, ETA в строке,
 * кнопка «маршрут» в результате, клавиатура ↑↓ Enter Shift+Enter Esc.
 * Оффлайн-first: весь индекс на устройстве.
 */
import { formatDuration, getLang, type I18nKey, tr } from '@app/i18n'
import { localized, type RoutePlan, resolveStart, shelfCodeOf, type Venue } from '@app/rvf'
import { useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { selectTarget, showRoute } from '../actions'
import { reportSearchMissed, reportSearchPick } from '../analytics'
import { planWithHazards } from '../emergency/safe-route'
import { markerVisual } from '../map/marker-icons'
import { promoPhase, promoTiers } from '../promo'
import {
  buildSearchIndex,
  isOpenNow,
  loadRecent,
  productPromoTiers,
  pushRecent,
  type SearchEntry,
  type SearchHit,
  type SearchKind,
  searchVenue,
  withPromoRow,
} from '../search'
import { addToList } from '../shopping'
import {
  anchorId,
  geoStart,
  graph,
  langTick,
  liveOccupants,
  searchOpen,
  venue,
  venueMap,
} from '../state'
import { dedupeEntries } from './dedupe'
import { categoryLabel, featureName } from './labels'

const KIND_TABS: readonly { key: SearchKind | 'all'; labelKey: I18nKey }[] = [
  { key: 'all', labelKey: 'search.tab_all' },
  { key: 'tenant', labelKey: 'search.tab_tenants' },
  { key: 'building', labelKey: 'search.tab_buildings' },
  { key: 'infra', labelKey: 'search.tab_infra' },
]

/** Табы площадки: «Товары» появляется только там, где есть товарный слой. */
function kindTabs(v: Venue): readonly { key: SearchKind | 'all'; labelKey: I18nKey }[] {
  return (v.products ?? []).length > 0
    ? [...KIND_TABS, { key: 'product', labelKey: 'search.tab_products' }]
    : KIND_TABS
}

const QUICK_INTENTS: readonly { labelKey: I18nKey; query: string }[] = [
  { labelKey: 'search.quick_food', query: 'кафе' },
  { labelKey: 'search.quick_warehouse', query: 'склад' },
  { labelKey: 'search.quick_dock', query: 'рампа' },
  { labelKey: 'search.quick_entrance', query: 'вход' },
  { labelKey: 'search.quick_parking', query: 'парковка' },
  { labelKey: 'search.quick_checkpoint', query: 'кпп' },
]

/** Быстрые запросы магазина: то, что спрашивают у продавца чаще всего. */
const RETAIL_INTENTS: readonly { labelKey: I18nKey; query: string }[] = [
  { labelKey: 'search.quick_milk', query: 'piim' },
  { labelKey: 'search.quick_bread', query: 'leib' },
  { labelKey: 'search.quick_checkout', query: 'kassa' },
  { labelKey: 'search.quick_wc', query: 'wc' },
  { labelKey: 'search.quick_entrance', query: 'вход' },
]

/** ETA до цели от текущего старта — кэш на время открытого поиска. */
function useEtaResolver(): (id: string) => RoutePlan | null {
  const cache = useMemo(() => new Map<string, RoutePlan | null>(), [])
  return (id: string) => {
    const v = venue.value
    const g = graph.value
    if (!v || !g) return null
    const cached = cache.get(id)
    if (cached !== undefined) return cached
    const start = resolveStart(v, {
      anchorId: anchorId.value ?? undefined,
      geo: geoStart.value ?? undefined,
    })
    // ETA — по пути в обход опасностей: время кратчайшего через огонь соврёт.
    const plan = start ? planWithHazards(v, start, id).plan : null
    cache.set(id, plan)
    return plan
  }
}

function noopHover(_: number): void {
  // строки «недавних»/каталога не участвуют в клавиатурной навигации
}

function closeSearch(): void {
  searchOpen.value = false
  venueMap.value?.highlightMany([])
}

function pick(id: string, route = false): void {
  const v = venue.value
  if (v) reportSearchPick(v.venue.id, id)
  pushRecent(id)
  closeSearch()
  selectTarget(id)
  if (route) showRoute()
}

function OpenBadge({ hours }: { hours: string }) {
  const open = isOpenNow(hours)
  if (open === null) return null
  return (
    <span class={`search-open-badge ${open ? 'is-open' : 'is-closed'}`}>
      {tr(open ? 'search.open_badge' : 'search.closed_badge')}
    </span>
  )
}

/** Имя строки: товар — имя товара, остальное — имя цели. */
function hitName(v: Venue, hit: SearchHit): string {
  if (hit.product) {
    const product = (v.products ?? []).find((p) => p.id === hit.product?.id)
    if (product) return localized(product.name, getLang(), v.venue.default_language)
  }
  return featureName(v, hit.id)
}

/** Подпись строки: товар — «полка · код» (ответ продавца), иначе категория. */
function hitSub(v: Venue, hit: SearchHit): string {
  if (hit.product) {
    const poi = v.pois.find((p) => p.id === hit.id)
    const code = poi ? shelfCodeOf(poi) : null
    return [featureName(v, hit.id), code ?? ''].filter(Boolean).join(' · ')
  }
  return [
    categoryLabel(hit.category),
    hit.floor !== null ? tr('search.floor_n', { n: String(hit.floor) }) : '',
  ]
    .filter(Boolean)
    .join(' · ')
}

function ResultRow(props: {
  hit: SearchHit
  eta: RoutePlan | null
  active: boolean
  idx: number
  onHover: (i: number) => void
}) {
  const v = venue.value
  if (!v) return null
  const { hit } = props
  const { svg, color } = markerVisual(hit.category)
  return (
    <div
      data-idx={props.idx}
      class={`search-row${props.active ? ' search-row-active' : ''}${
        hit.promo ? ' promo-row' : ''
      }`}
      onMouseEnter={() => {
        props.onHover(props.idx)
        venueMap.value?.highlightMany([props.hit.id])
      }}
    >
      <button type="button" class="search-row-main" onClick={() => pick(hit.id)}>
        <span
          class="search-row-icon"
          style={{ '--mk': color }}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
        <span class="search-row-text">
          <span class="search-row-name">
            {hitName(v, hit)}
            {hit.hours && <OpenBadge hours={hit.hours} />}
            {hit.viaTag && <i class="search-row-tag">{tr('search.usually_here')}</i>}
            {hit.promo && <i class="promo-chip">{tr('promo.chip')}</i>}
          </span>
          <span class="search-row-sub">{hitSub(v, hit)}</span>
        </span>
      </button>
      <RowActions hit={hit} eta={props.eta} venueId={v.venue.id} />
    </div>
  )
}

/** Кнопки строки: «+ в список» (товар), ETA, «маршрут». */
function RowActions(props: { hit: SearchHit; eta: RoutePlan | null; venueId: string }) {
  return (
    <>
      {props.hit.product && (
        <button
          type="button"
          class="search-row-add"
          title={tr('shopping.add')}
          aria-label={tr('shopping.add')}
          onClick={() => addToList(props.venueId, props.hit.key)}
        >
          +
        </button>
      )}
      {props.eta && <span class="search-row-eta">{formatDuration(props.eta.durationS)}</span>}
      <button
        type="button"
        class="search-row-go"
        title={tr('search.route_btn')}
        aria-label={tr('search.route_btn')}
        onClick={() => pick(props.hit.id, true)}
      >
        ➤
      </button>
    </>
  )
}

/** Пустой запрос: быстрые интенты, недавнее, все объекты списком. */
function SearchIdle(props: {
  hasProducts: boolean
  index: readonly SearchEntry[]
  recent: readonly string[]
  browse: readonly SearchHit[]
  etaOf: (id: string) => RoutePlan | null
  onIntent: (q: string) => void
}) {
  const intents = props.hasProducts ? RETAIL_INTENTS : QUICK_INTENTS
  return (
    <div class="search-idle">
      <p class="search-section-title">✦ {tr('search.quick_title')}</p>
      <div class="search-intents">
        {intents.map((q) => (
          <button
            key={q.query}
            type="button"
            class="search-intent"
            onClick={() => props.onIntent(q.query)}
          >
            {tr(q.labelKey)}
          </button>
        ))}
      </div>
      {props.recent.length > 0 && (
        <>
          <p class="search-section-title">↺ {tr('search.recent')}</p>
          {props.recent.map((id, i) => {
            const entry = props.index.find((e) => e.id === id)
            if (!entry) return null
            return (
              <ResultRow
                key={id}
                idx={i}
                active={false}
                onHover={noopHover}
                hit={{ ...entry, viaTag: null, score: 0 }}
                eta={props.etaOf(id)}
              />
            )
          })}
        </>
      )}
      {props.browse.length > 0 && (
        <>
          <p class="search-section-title">⌸ {tr('search.all_objects')}</p>
          {props.browse.map((hit, i) => (
            <ResultRow
              key={hit.id}
              idx={props.recent.length + i}
              active={false}
              onHover={noopHover}
              hit={hit}
              eta={props.etaOf(hit.id)}
            />
          ))}
        </>
      )}
    </div>
  )
}

/** Палитра поиска. */
export function SearchPanel() {
  langTick.value
  const v = venue.value
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<SearchKind | 'all'>('all')
  const [openNow, setOpenNow] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  const [missedSent, setMissedSent] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)
  const etaOf = useEtaResolver()

  const open = searchOpen.value
  useEffect(() => {
    if (!open) {
      setQuery('')
      setKind('all')
      setOpenNow(false)
    }
    setActiveIdx(0)
  }, [open])
  useEffect(() => setActiveIdx(0), [query, kind, openNow])
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [activeIdx])

  if (!v || !open) return null

  const index = buildSearchIndex(v, liveOccupants.value)
  // Оплаченное продвижение добавляется ПОСЛЕ ранжирования и не двигает
  // найденное: порядок ответа на «где это» деньгами не покупается (ADR 0019).
  // Товарные промо — только в спокойном ОБЗОРЕ, как и все остальные.
  const promoMap = new Map([
    ...promoTiers.value,
    ...(promoPhase() === 'overview' ? productPromoTiers(v) : new Map()),
  ])
  const hits = withPromoRow(
    searchVenue(index, query, undefined, { kind, openNow }),
    index,
    promoMap,
  )
  const hasQuery = query.trim().length >= 2
  if (hasQuery) venueMap.value?.highlightMany(hits.map((h) => h.id))
  const recent = !hasQuery ? loadRecent().filter((id) => index.some((e) => e.id === id)) : []
  const hasHours = index.some((e) => e.hours !== '')
  // Пустой запрос: ВСЕ фирмы и здания списком (фидбек владельца 27.07).
  const browse = !hasQuery
    ? dedupeEntries(
        v,
        index.filter((e) => (kind === 'all' ? e.kind !== 'infra' : e.kind === kind)),
      )
        .map((e) => ({ ...e, viaTag: null, score: 0 }))
        .sort((a, b) =>
          a.kind === b.kind
            ? featureName(v, a.id).localeCompare(featureName(v, b.id), 'ru')
            : a.kind === 'tenant'
              ? -1
              : 1,
        )
    : []

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => Math.min(i + 1, Math.max(0, hits.length - 1)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const hit = hits[activeIdx]
      if (hit) pick(hit.id, e.shiftKey)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      closeSearch()
    }
  }

  const reportMissed = (): void => {
    reportSearchMissed(v.venue.id, query)
    setMissedSent(true)
  }

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: клик по фону закрывает; клавиатура — Esc в поле
    <div class="search-overlay" onClick={closeSearch}>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-обёртка, не интерактив */}
      <div class="search-card glass" onClick={(e) => e.stopPropagation()}>
        <div class="search-head">
          <span class="search-head-icon" aria-hidden="true">
            ⌕
          </span>
          <input
            class="search-input"
            type="search"
            ref={(el) => el?.focus()}
            placeholder={tr('search.placeholder')}
            value={query}
            onKeyDown={onKeyDown}
            onInput={(e) => {
              setQuery((e.target as HTMLInputElement).value)
              setMissedSent(false)
            }}
          />
          <button
            type="button"
            class="icon-btn"
            aria-label={tr('common.close')}
            onClick={closeSearch}
          >
            ✕
          </button>
        </div>

        <div class="search-tabs">
          {kindTabs(v).map((t) => (
            <button
              key={t.key}
              type="button"
              class={`search-tab${kind === t.key ? ' search-tab-on' : ''}`}
              onClick={() => setKind(t.key)}
            >
              {tr(t.labelKey)}
            </button>
          ))}
          {hasHours && (
            <>
              <span class="search-tabs-sep" />
              <button
                type="button"
                class={`search-tab search-tab-open${openNow ? ' search-tab-on-ok' : ''}`}
                onClick={() => setOpenNow((x) => !x)}
              >
                ● {tr('search.open_now')}
              </button>
            </>
          )}
        </div>

        <div class="search-body" ref={listRef}>
          {!hasQuery && (
            <SearchIdle
              hasProducts={(v.products ?? []).length > 0}
              index={index}
              recent={recent}
              browse={browse}
              etaOf={etaOf}
              onIntent={setQuery}
            />
          )}

          {hasQuery &&
            hits.map((hit, i) => (
              <ResultRow
                key={hit.id}
                idx={i}
                active={i === activeIdx}
                onHover={setActiveIdx}
                hit={hit}
                eta={etaOf(hit.id)}
              />
            ))}

          {hasQuery && hits.length === 0 && (
            <div class="search-missed">
              <p>{tr('search.not_found', { q: query.trim() })}</p>
              <p class="search-missed-hint">{tr('search.smart_hint')}</p>
              {missedSent ? (
                <p class="search-missed-ok">{tr('search.report_sent')}</p>
              ) : (
                <button type="button" class="btn-secondary" onClick={reportMissed}>
                  {tr('search.report')}
                </button>
              )}
            </div>
          )}
        </div>

        <div class="search-foot">
          <span class="search-foot-keys">
            ↑↓ · Enter {tr('search.hint_open')} · Shift+Enter {tr('search.hint_route')} · Esc
          </span>
          {hasQuery && <span>{tr('search.found_n', { n: String(hits.length) })}</span>}
        </div>
      </div>
    </div>
  )
}
